import {
  getMessageFromNetworkError,
  useDeleteFolderAncestors,
  useMoveFolder,
} from "@zapier/client";
import { getFolderUrl, useNotification } from "@zapier/utils";
import { Link, Text } from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";

interface Input {
  optimisticUpdateEnabled: boolean;
  assetTitle: string | undefined | null;
}

export function useMoveFolderActions({
  optimisticUpdateEnabled,
  assetTitle,
}: Input) {
  const t = useTranslations("move-folder-actions");
  const { createNotification } = useNotification();

  const { moveFolder } = useMoveFolder({ optimisticUpdateEnabled });
  const { deleteFolderAncestors } = useDeleteFolderAncestors({
    optimisticUpdateEnabled,
  });

  // Move folders between folders
  const moveFolderWithToasts = ({
    parentId,
    rootId,
    currentFolderId,
    selectedFolderId,
    selectedFolderName,
  }) => {
    moveFolder(
      {
        parentId,
        folderId: currentFolderId,
        rootId,
      },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.move-success", {
                  assetTitle: assetTitle || "",
                  selectedFolderName,
                  currentFolderLink: (chunks) => (
                    <Link href={getFolderUrl(currentFolderId)}>{chunks}</Link>
                  ),
                  folderLink: (chunks) => (
                    <Link href={getFolderUrl(selectedFolderId)}>{chunks}</Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-move", { assetTitle: assetTitle || "" }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );
    return;
  };

  // Unnest a folder
  const unnestWithToasts = ({ folderId }) => {
    deleteFolderAncestors(
      { folderId },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t.rich("notifications.unnest-success", {
                  assetTitle: assetTitle || "",
                  folderLink: (chunks) => (
                    <Link href={getFolderUrl(folderId)}>{chunks}</Link>
                  ),
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: t("errors.unable-to-unnest", {
              assetTitle: assetTitle || "",
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );
    return;
  };

  return {
    moveFolderWithToasts,
    unnestWithToasts,
  };
}
